// The radio's decisions, as plain functions: what plays next, how loud the engineering context wants it, when a
// station ident is due. No audio and no DOM here, so all of it is unit-tested.
import type { Environment, Intensity, RadioTrack } from "./catalogue";

/** What the learner is doing, as the views report it. The radio listens; nothing engineering depends on it. */
export interface RadioContext { environment?: Environment; activity?: string }

/** Reading the room: quieter while reviewing or reading, normal while building, more energy allowed while commissioning. */
export function contextMood(ctx: RadioContext): { level: number; prefer?: { environment?: Environment; intensity?: Intensity[] } } {
  const a = ctx.activity ?? "";
  const level = a === "review" || a === "dreview" ? .72 : ["spec", "design", "drawings", "learning", "overview"].includes(a) ? .85 : 1;
  const intensity: Intensity[] | undefined = a === "commission" ? ["high", "medium"] : level < 1 ? ["low", "medium"] : undefined;
  return { level, prefer: ctx.environment || intensity ? { environment: ctx.environment, intensity } : undefined };
}

/** The station's order for this visit: `first` always opens it, the rest come shuffled after it (so `first` never plays
    again until every other song has had its turn). `rand` is there for the tests. */
export function stationOrder(list: RadioTrack[], first: string, rand: () => number = Math.random): RadioTrack[] {
  const lead = list.filter(t => t.id === first), rest = list.filter(t => t.id !== first);
  for (let i = rest.length - 1; i > 0; i--) { const j = Math.floor(rand() * (i + 1)); [rest[i], rest[j]] = [rest[j], rest[i]]; }
  return lead.concat(rest);
}

/** The track after `currentId`: the next one in order, unless one of the next few fits the work better. */
export function nextTrack(list: RadioTrack[], currentId: string | undefined, ctx: RadioContext = {}): RadioTrack {
  const i = list.findIndex(t => t.id === currentId);
  const order = list.slice(i + 1).concat(list.slice(0, i + 1)).filter((t, k, a) => a.length === 1 || t.id !== currentId);
  // look a few tracks ahead for one that suits the work, but never pull the station's opener forward over the round's last songs
  const wrap = order.indexOf(list[0]), want = contextMood(ctx).prefer, ahead = order.slice(0, wrap < 0 ? 3 : Math.min(3, wrap || 1));
  const fits = (t: RadioTrack) => (!want?.environment || t.environment === want.environment) && (!want?.intensity || want.intensity.includes(t.intensity));
  return ahead.find(fits) ?? ahead.find(t => !!want?.environment && t.environment === want.environment) ?? order[0] ?? list[0];
}

export function previousTrack(list: RadioTrack[], currentId: string | undefined): RadioTrack {
  const i = list.findIndex(t => t.id === currentId);
  return list[(i - 1 + list.length) % list.length] ?? list[0];
}

/** The next n tracks as they would play now. */
export function upNext(list: RadioTrack[], currentId: string | undefined, ctx: RadioContext = {}, n = 2): RadioTrack[] {
  const out: RadioTrack[] = []; let id = currentId;
  for (let k = 0; k < Math.min(n, list.length - 1); k++) { const t = nextTrack(list, id, ctx); out.push(t); id = t.id; }
  return out;
}

/** Idents are special: one after every third track change at most, never two in a row. */
export const identDue = (changes: number, lastIdentAt: number) => changes > 0 && changes % 3 === 0 && changes - lastIdentAt >= 3;

export const clock = (s: number) => !isFinite(s) || s < 0 ? "0:00" : `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
